import { useEffect, useState, useRef } from 'react';
import { Progress } from '@/components/ui/progress';

interface ProgressPhase {
  level: 'standard' | 'extended' | 'persistent';
  minSeconds: number;
  maxSeconds: number;
  targetProgressPercent: number;
  stages?: Array<{ maxSeconds: number; label: string }>;
  message?: string;
  tip?: string;
  tipStartSeconds?: number;
}

const PROGRESS_CONFIG: ProgressPhase[] = [
  {
    level: 'standard',
    minSeconds: 0,
    maxSeconds: 90,
    targetProgressPercent: 70,
    tipStartSeconds: 45,
    tip: 'Extraction time varies based on document complexity. Most policies complete in 45-90 seconds',
    stages: [
      { maxSeconds: 5, label: 'Uploading documents' },
      { maxSeconds: 25, label: 'Detecting policy type' },
      { maxSeconds: 45, label: 'Reading policy structure' },
      { maxSeconds: 65, label: 'Extracting coverage details' },
      { maxSeconds: 85, label: 'Identifying premiums' },
      { maxSeconds: 90, label: 'Extracting benefit information' },
    ],
  },
  {
    level: 'extended',
    minSeconds: 90,
    maxSeconds: 180,
    targetProgressPercent: 95,
    message: 'Processing your policy',
    tip: 'Complex policies can take 2-3 minutes. Extracting relevant details from long documents can take additional time',
  },
  {
    level: 'persistent',
    minSeconds: 180,
    maxSeconds: Infinity,
    targetProgressPercent: 99,
    message: 'Finalizing extraction',
    tip: 'Finalizing your policy data. Comprehensive extractions can take several minutes during high traffic periods',
  },
];

function getCurrentPhase(elapsedSeconds: number): ProgressPhase {
  return (
    PROGRESS_CONFIG.find(
      phase => elapsedSeconds >= phase.minSeconds && elapsedSeconds < phase.maxSeconds,
    ) || PROGRESS_CONFIG[PROGRESS_CONFIG.length - 1]!
  );
}

function calculateProgress(elapsedSeconds: number, phase: ProgressPhase): number {
  const LOG_SCALE_FACTOR = 35;
  const PERSISTENT_SECONDS_PER_PERCENT = 30;
  switch (phase.level) {
    case 'standard':
      //Log curve to show smooth acceleration in the begining and slow down towards the end of the phase.
      return Math.min(
        phase.targetProgressPercent,
        LOG_SCALE_FACTOR * Math.log10(elapsedSeconds + 1),
      );
    case 'extended':
      //Linear progress
      const standardPhase = PROGRESS_CONFIG.find(p => p.level === 'standard')!;
      const secondsInExtendedPhase = elapsedSeconds - phase.minSeconds;
      const phaseDuration = phase.maxSeconds - phase.minSeconds;
      const progressRange = phase.targetProgressPercent - standardPhase.targetProgressPercent;
      return Math.min(
        phase.targetProgressPercent,
        standardPhase.targetProgressPercent +
          (secondsInExtendedPhase / phaseDuration) * progressRange,
      );
    case 'persistent':
      //Slowest linear progress as we need to show incremental progress until we get the response
      const extendedPhase = PROGRESS_CONFIG.find(p => p.level === 'extended')!;
      const secondsInPersistentPhase = elapsedSeconds - phase.minSeconds;
      return Math.min(
        phase.targetProgressPercent,
        extendedPhase.targetProgressPercent +
          secondsInPersistentPhase / PERSISTENT_SECONDS_PER_PERCENT,
      );
  }
}

function getCurrentLabel(elapsedSeconds: number, phase: ProgressPhase): string {
  if (phase.stages) {
    const stage = phase.stages.find(s => elapsedSeconds <= s.maxSeconds);
    return stage?.label || phase.stages[phase.stages.length - 1]!.label;
  }
  return phase.message || 'Processing';
}

export function PolicyExtractorProgressBar() {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const phase = getCurrentPhase(elapsedSeconds);
  const progress = calculateProgress(elapsedSeconds, phase);
  const label = getCurrentLabel(elapsedSeconds, phase);
  const showTip =
    phase.tip && (phase.tipStartSeconds ? elapsedSeconds >= (phase.tipStartSeconds || 0) : true);

  return (
    <div className="mb-2 mt-4 flex flex-col gap-2 md:mb-0 md:mt-1">
      <div className="flex justify-between">
        <p className="text-xs text-gray-600">{label}...</p>
        <p className="text-xs text-gray-500">{Math.round(progress)}%</p>
      </div>
      <Progress
        value={progress}
        className="bg-primary-100 h-2 w-full"
      />
      {showTip && <p className="text-xs text-gray-500">{phase.tip}</p>}
    </div>
  );
}
